"use client";

import { useState, useRef, useEffect } from "react";
import MessageInput from "./MessageInput";
import Messages from "./Messages";
import { TiMessages } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { IoEllipsisVertical, IoClose } from "react-icons/io5";
import { FaUser } from "react-icons/fa";

import ParticipantsModal from "./ParticipantsModal";
import userProfile from "../../../assets/Images/user.png";
import userProfileLight from "../../../assets/Images/user2.png";
import groupProfile from "../../../assets/Images/group.png";
import groupProfileLight from "../../../assets/Images/users2.png";

import { setSelectedConversation } from "../../Slices/ChatSlice";
import ProfilePictureModal from "../ProfilePictureModal";
import MediaFilesModal from "./MediaFilesModal";

const MessageContainer = ({ onlineUsers }) => {
  const conversationKey = useSelector((state) => {
    const conversation = state.conversation;
    return `${conversation?.conversationType || "simple"}:${conversation?.selectedConversation?.value ?? "none"}`;
  });
  // Local edit/reply/attachment state belongs only to this conversation.
  // Remounting also lets the outgoing composer save its own draft on cleanup.
  return <ConversationMessageContainer key={conversationKey} onlineUsers={onlineUsers} />;
};

const ConversationMessageContainer = ({ onlineUsers }) => {
  const dispatch = useDispatch();
  const usersData = useSelector((state) => state?.data?.practice?.users?.value);
  const selectedConversationData = useSelector((state) => state.conversation);
  const selectedConversationType = selectedConversationData?.conversationType;
  const selectedConversation = selectedConversationData?.selectedConversation;

  const [droppedFile, setDroppedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [showMediaFilesModal, setShowMediaFilesModal] = useState(false);

  const menuRef = useRef(null);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChatClose = () => {
    dispatch(
      setSelectedConversation({
        conversation: null,
        type: selectedConversationType,
      })
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (selectedConversation && file) {
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxFileSize) {
        toast.error("File size exceeds 10MB. Please upload a smaller file.");
        return;
      }
      setDroppedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const clearDroppedFile = () => {
    setDroppedFile(null);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setShowParticipantsModal(false);
    setIsModalOpen(false);
  };

  const handleProfilePictureClick = () => {
    setShowProfilePictureModal(true);
  };

  return (
    <div
      className="w-full min-w-0 min-h-0 flex flex-col h-full"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      {!selectedConversation ? (
        <NoChatSelected />
      ) : (
        <>
          {/* Header */}
          <div className="bg-sideNavbg rounded-lg px-4 py-3 mb-2 flex shrink-0 items-center justify-between m-4">
            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={
                    selectedConversation?.profilePicture
                      ? `data:image/png;base64,${selectedConversation?.profilePicture}`
                      : selectedConversationType === "group" &&
                        selectedConversation?.value ===
                          selectedConversation.value
                      ? groupProfileLight
                      : selectedConversationType === "group" &&
                        selectedConversation?.value !==
                          selectedConversation.value
                      ? groupProfile
                      : selectedConversationType === "simple" &&
                        selectedConversation?.value ===
                          selectedConversation.value
                      ? userProfileLight
                      : userProfile
                  }
                  alt={selectedConversation?.label}
                  className="w-10 h-10 rounded-full object-cover cursor-pointer"
                  onClick={handleProfilePictureClick}
                />
                {selectedConversationType === "simple" && (
                  <span
                    className={`absolute bottom-0 right-0 w-2.5 h-2.5  ${
                      onlineUsers[selectedConversation?.value] === true ||
                      onlineUsers[selectedConversation?.value] === "true"
                        ? "bg-green-500"
                        : "bg-gray-400"
                    } rounded-full shadow-md`}
                  ></span>
                )}
              </div>
              <div
                className={`flex ${
                  !selectedConversation?.participants && " flex-col"
                }`}
              >
                <span className="text-white font-bold">
                  {selectedConversation?.label}
                </span>
                {selectedConversation?.participants && (
                  <>
                    <p
                      title="Participants"
                      className="flex items-center  text-white ml-2 "
                    >
                      |
                    </p>
                    <p
                      title="Participants"
                      className="flex items-center  text-white ml-2 gap-1"
                    >
                      <FaUser className="text-xs" />
                      <span className="text-sm text-center">
                        {selectedConversation.participants.length}
                      </span>{" "}
                    </p>
                  </>
                )}
                {selectedConversationType === "simple" && (
                  <span className="text-blue-200 text-xs ">
                    {onlineUsers[selectedConversation?.value] === true ||
                    onlineUsers[selectedConversation?.value] === "true"
                      ? "Online"
                      : "Offline"}
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-4">
              {selectedConversationType === "group" && (
                <div className="relative" ref={menuRef}>
                  <button
                    className="text-white hover:bg-blue-600 p-2 rounded-full transition-colors"
                    onClick={() => setShowMenu(!showMenu)}
                    aria-label="More options"
                  >
                    <IoEllipsisVertical className="w-5 h-5" />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-50">
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
                        onClick={() => {
                          openModal();
                          setShowMenu(false);
                        }}
                      >
                        View Participants (
                        {selectedConversation?.participants?.length || 0})
                      </button>

                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
                        onClick={() => {
                          setShowMediaFilesModal(true);
                          setShowMenu(false);
                        }}
                        aria-label="View media files"
                        title="View Media Files"
                      >
                        View Media Files
                      </button>
                    </div>
                  )}
                </div>
              )}
              {selectedConversationType === "simple" && (
                <div className="relative" ref={menuRef}>
                  <button
                    className="text-white hover:bg-blue-600 p-2 rounded-full transition-colors"
                    onClick={() => setShowMenu(!showMenu)}
                    aria-label="More options"
                  >
                    <IoEllipsisVertical className="w-5 h-5" />
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg z-50">
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100"
                        onClick={() => {
                          setShowMediaFilesModal(true);
                          setShowMenu(false);
                        }}
                        aria-label="View media files"
                        title="View Media Files"
                      >
                        View Media Files
                      </button>
                    </div>
                  )}
                </div>
              )}

              <button
                className="text-white hover:bg-blue-600 p-2 rounded-full transition-colors"
                aria-label="Close chat"
                onClick={handleChatClose}
              >
                <IoClose className="w-5 h-5" />
              </button>
            </div>
          </div>

          <Messages
            setReplyingTo={setReplyingTo}
            setEditingMessage={setEditingMessage}
            editingMessage={editingMessage}
          />
          <MessageInput
            droppedFile={droppedFile}
            clearDroppedFile={clearDroppedFile}
            setReplyingTo={setReplyingTo}
            replyingTo={replyingTo}
            editingMessage={editingMessage}
            setEditingMessage={setEditingMessage}
            selectedConversationData={selectedConversationData}
          />

          {/* Participants Modal */}
          {isModalOpen && (
            <ParticipantsModal
              selectedConversation={selectedConversation}
              showParticipantsModal={showParticipantsModal}
              setShowParticipantsModal={setShowParticipantsModal}
              onCloseParticipantsModal={closeModal}
              usersData={usersData}
            />
          )}

          {/* Profile Picture Modal */}
          {showProfilePictureModal && (
            <ProfilePictureModal
              profilePicture={
                selectedConversation?.profilePicture
                  ? `data:image/png;base64,${selectedConversation?.profilePicture}`
                  : selectedConversationType === "group"
                  ? groupProfile
                  : userProfile
              }
              onClose={() => setShowProfilePictureModal(false)}
            />
          )}

          {/* Media Files Modal */}
          {showMediaFilesModal && (
            <MediaFilesModal
              selectedConversation={selectedConversation}
              onClose={() => setShowMediaFilesModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

const NoChatSelected = () => (
  <div className="flex flex-col items-center justify-center h-full">
    <TiMessages size={50} className="text-gray-400" />
    <p className="text-gray-400 mt-4">
      Hi, please select a conversation to start chatting.
    </p>
  </div>
);

export default MessageContainer;
